import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentSms, faXmark } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Checkbox } from 'components/UI/V3/Forms/Checkbox/Checkbox';
import { SMSContent } from './SMSContentEditor';
import styles from './SMSSidebar.module.scss';

export interface SMSStepConfig {
    name: string;
    content: SMSContent;
    skipCampaign: boolean;
    skipFlow: boolean;
}

interface Props {
    initialConfig?: SMSStepConfig;
    onSave?: (config: SMSStepConfig) => void;
    onCancel?: () => void;
    onEditContent?: () => void;
    onConfigChange?: (config: SMSStepConfig) => void;
}

const DEFAULT_UNSUBSCRIBE = 'Reply STOP to unsubscribe';

const SMSSidebar: FunctionComponent<Props> = ({ initialConfig, onSave, onCancel, onEditContent, onConfigChange }) => {
    const [name, setName] = useState(initialConfig?.name ?? 'SMS #1');
    const [content, setContent] = useState<SMSContent>(
        initialConfig?.content ?? { message: '', unsubscribeMessage: DEFAULT_UNSUBSCRIBE }
    );
    const [skipCampaign, setSkipCampaign] = useState(initialConfig?.skipCampaign ?? true);
    const [skipFlow, setSkipFlow] = useState(initialConfig?.skipFlow ?? true);

    const isMounted = useRef(false);
    // Tracks when content is being synced from an external source (content editor).
    // Prevents the onConfigChange effect from firing in response to this sync,
    // since the step was already updated upstream by updateSMSStepContent.
    const syncingFromExternal = useRef(false);

    // Sync content when updated externally (e.g. from the full-screen editor)
    useEffect(() => {
        if (initialConfig?.content) {
            syncingFromExternal.current = true;
            setContent(initialConfig.content);
        }
    }, [initialConfig?.content?.message, initialConfig?.content?.unsubscribeMessage]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        // Skip the change notification if it was triggered by an external sync.
        // The step was already updated via updateSMSStepContent in that case.
        if (syncingFromExternal.current) {
            syncingFromExternal.current = false;
            return;
        }
        onConfigChange?.({ name, content, skipCampaign, skipFlow });
    }, [name, content, skipCampaign, skipFlow]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = () => {
        onSave?.({ name, content, skipCampaign, skipFlow });
    };

    const hasContent = content.message.trim().length > 0;

    return (
        <aside className={styles.sidebar}>
            <div className={styles.body}>
                {/* Header */}
                <div className={styles.nodeHeader}>
                    <div className={styles.iconCircle}>
                        <FontAwesomeIcon icon={faCommentSms} />
                    </div>
                    <div className={styles.headerInfo}>
                        <span className={styles.headerTitle}>Send SMS</span>
                    </div>
                    {onCancel && (
                        <button
                            type="button"
                            className={styles.closeBtn}
                            onClick={onCancel}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    )}
                </div>

                {/* SMS Name */}
                <div className={styles.section}>
                    <div className={styles.sectionLabel}>SMS Name</div>
                    <Input
                        value={name}
                        onChange={e => setName((e.target as HTMLInputElement).value)}
                        placeholder="SMS #1"
                        autoFocus={false}
                    />
                </div>

                <hr className={styles.divider} />

                {/* Content */}
                <div className={styles.section}>
                    <div className={styles.sectionLabel}>Content</div>
                    <div className={styles.contentRow}>
                        <div className={styles.contentPreview}>
                            {hasContent ? (
                                <>
                                    <p className={styles.contentPreviewText}>{content.message}</p>
                                    <p className={styles.contentPreviewUnsub}>{content.unsubscribeMessage}</p>
                                </>
                            ) : (
                                <p className={styles.contentPreviewEmpty}>No content yet</p>
                            )}
                        </div>
                        <button
                            type="button"
                            className={styles.editBtn}
                            onClick={onEditContent}
                        >
                            Edit
                        </button>
                    </div>
                </div>

                <hr className={styles.divider} />

                {/* Skip options */}
                <div className={styles.section}>
                    <div className={styles.skipItem}>
                        <Checkbox
                            checked={skipCampaign}
                            onChange={setSkipCampaign}
                        />
                        <div className={styles.skipText}>
                            <span className={styles.skipLabel}>Skip clients who have received a campaign message</span>
                            <span className={styles.skipSub}>Within the last 12 hours</span>
                        </div>
                    </div>
                    <div className={styles.skipItem}>
                        <Checkbox
                            checked={skipFlow}
                            onChange={setSkipFlow}
                        />
                        <div className={styles.skipText}>
                            <span className={styles.skipLabel}>
                                Skip clients another automation messaged in the last 7 days
                            </span>
                            <span className={styles.skipSub}>Within the last 12 hours</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.footer}>
                <Button
                    label="Save"
                    variant="green"
                    fullWidth
                    disabled={!name.trim()}
                    onClick={handleSave}
                />
            </div>
        </aside>
    );
};

export default SMSSidebar;
